if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          mediaGallery: "[id^=Media-Gallery]",
          liveRegion: '[id^="GalleryStatus"]',
          viewer: '[id^="GalleryViewer"]',
          thumbnailsWrapper: ".f-product__media-thumbnails-wrapper",
          thumbnails: '[id^="GalleryThumbnails"]',
          thumbnailItems: ['[id^="GalleryThumbnailItem"]'],
          videos: [".f-video__embed"],
          models: ["product-model"],
          medias: [".f-product__media"],
          xrButton: "[data-first-xr-button]",
          sliderCounter: ".flickity-counter--current",
          toggleZoom: [".js-photoswipe--zoom"],
        };

        let touchingCarousel = false;
        let startXCoordinate;

        document.body.addEventListener(
          "touchstart",
          (e) => {
            if (e.target.closest(".flickity-slider")) {
              touchingCarousel = true;
            } else {
              touchingCarousel = false;
              return;
            }
            startXCoordinate = e.touches[0].pageX;
          },
          { passive: true }
        );

        document.body.addEventListener(
          "touchmove",
          (e) => {
            if (touchingCarousel && e.cancelable) {
              if (Math.abs(e.touches[0].pageX - startXCoordinate) > 10) {
                e.preventDefault();
              }
            }
          },
          { passive: false }
        );
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        clearInterval(this.check);
      }

      getSelectedVariant() {
        const selectedVariant = this.querySelector(
          "[data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      init() {
        this.domNodes = queryDomNodes(this.selectors, this);
        this.mediaLayout = this.domNodes.mediaGallery.dataset.mediaLayout;
        // 'carousel-vertical' behaves like 'carousel', with the thumbnails
        // stacked in a fixed-width column instead of a row on desktop.
        this.isVerticalThumbs = this.mediaLayout === "carousel-vertical";
        this.isCarousel = this.mediaLayout === "carousel" || this.isVerticalThumbs;
        this.onlyImage = this.dataset.onlyImage === "true";
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest("product-info");
        this.enableZoom = this.dataset.enableZoom === "true";
        this.enableVariantGroupImages =
          this.dataset.enableVariantGroupImages === "true";
        this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true";
        this.variantPickers = this.section.querySelector(
          "[data-variant-picker]"
        );
        this.disableDefaultVariant =
          this.variantPickers &&
          this.variantPickers.dataset.disableDefaultVariant === "true";
        this.variantData = this.getVariantData();
        const selectedVariantId =
          (this.section &&
            this.section.querySelector('input[name="id"]')?.value) ||
          this.getSelectedVariant();
        this.currentVariant = this.variantData.find(
          (variant) => variant.id === Number(selectedVariantId)
        );

        if (!this.enableVariantGroupImages || this.onlyImage) {
          setTimeout(() => {
            this.removeAttribute("data-media-loading");
          }, 100);
        }
        if (this.isCarousel) {
          setTimeout(() => {
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
          });
          this.initGallerySlider();
        } else {
          const initSliders = () => {
            this.domNodes.mediaGallery && this.domNodes.mediaGallery.destroy();
            this.domNodes.thumbnails && this.domNodes.thumbnails.destroy();
            this.domNodes.mediaGallery &&
              this.domNodes.mediaGallery.initSlider();
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
            this.mediaGallery = this.domNodes.mediaGallery.slider.instance;
            this.thumbnails =
              this.domNodes.thumbnails &&
              this.domNodes.thumbnails.slider.instance;
            // Skip Flickity's watchCSS resize handler while in fullscreen so the
            // fullscreen-induced orientation change can't deactivate the slider.
            // The handler itself is kept, otherwise the slider can never recover
            // when flickity-component.css applies after the slider was created.
            this.skipResizeInFullscreen(this.mediaGallery);
            this.skipResizeInFullscreen(this.thumbnails);
            // flickity-component.css is loaded asynchronously, so watchCSS may run
            // before its ":after { content: 'flickity' }" rule applies and leave the
            // gallery rendered as a static grid on mobile. Re-check until it applies.
            this.ensureSliderActive(this.mediaGallery, () =>
              this.setActiveMediaOnLoad()
            );
            this.ensureSliderActive(this.thumbnails);
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));
            this.setActiveMediaOnLoad();
          };
          if (FoxThemeSettings.isMobile) {
            initSliders();
          }
          document.addEventListener("matchMobile", initSliders);
          document.addEventListener("unmatchMobile", () => {
            if (document.fullscreenElement || document.webkitFullscreenElement) return;
            this.domNodes.mediaGallery && this.domNodes.mediaGallery.destroy();
            this.domNodes.thumbnails && this.domNodes.thumbnails.destroy();
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          });
          const onFullscreenExit = () => {
            if (!document.fullscreenElement && !document.webkitFullscreenElement && FoxThemeSettings.isMobile) {
              initSliders();
            }
          };
          document.addEventListener("fullscreenchange", onFullscreenExit);
          document.addEventListener("webkitfullscreenchange", onFullscreenExit);
          if (this.enableZoom) this.initImageZoom();
        }
        if (this.onlyImage && this.enableZoom) this.initImageZoom();

        setTimeout(() => {
          this.dispatchEvent(
            new CustomEvent("product-media:loaded", {
              bubbles: true,
              detail: {
                currentVariant: this.currentVariant,
              },
            })
          );
        }, 100);
      }

      update() {
        if (this.domNodes.mediaGallery) this.domNodes.mediaGallery.update();
        if (this.domNodes.thumbnails) this.domNodes.thumbnails.update();
      }

      setActiveMediaOnLoad() {
        if (
          this.currentVariant &&
          this.currentVariant.featured_media &&
          !this.disableDefaultVariant &&
          !this.showFeaturedMedia
        ) {
          this.setActiveMedia(this.currentVariant.featured_media.id);
        }
      }

      skipResizeInFullscreen(slider) {
        if (!slider || slider.fullscreenResizeGuarded) return;
        slider.fullscreenResizeGuarded = true;
        const onResize = slider.onresize.bind(slider);
        slider.onresize = () => {
          if (document.fullscreenElement || document.webkitFullscreenElement)
            return;
          onResize();
        };
      }

      ensureSliderActive(slider, onActivated = null, retries = 20) {
        if (!slider || slider.isActive || retries <= 0) return;
        slider.watchCSS();
        if (slider.isActive) {
          onActivated && onActivated();
          return;
        }
        setTimeout(
          () => this.ensureSliderActive(slider, onActivated, retries - 1),
          100
        );
      }

      initGallerySlider() {
        if (!this.domNodes.medias.length) return;
        this.selectedSlide = null;
        this.selectedMediaType = "";
        this.check = setInterval(() => {
          this.mediaGallery =
            this.domNodes.mediaGallery.slider &&
            this.domNodes.mediaGallery.slider.instance;
          this.thumbnails =
            this.domNodes.thumbnails &&
            this.domNodes.thumbnails.slider &&
            this.domNodes.thumbnails.slider.instance;

          if (this.mediaGallery && typeof this.mediaGallery == "object") {
            clearInterval(this.check);
            // flickity-component.css is loaded asynchronously, so watchCSS may have
            // run before its ":after { content: 'flickity' }" rule applied.
            this.ensureSliderActive(this.mediaGallery);
            this.ensureSliderActive(this.thumbnails);
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));

            if (this.thumbnails) {
              if (this.thumbnails.cells && this.thumbnails.cells[0]) {
                this.thumbnails.select(0);
                this.handleScreenChange();
              }
            }
            if (this.isVerticalThumbs) this.initVerticalThumbnails();
            if (this.enableZoom) this.initImageZoom();
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          }
        }, 100);
      }

      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(
            this.querySelector('[type="application/json"]').textContent
          );
        return this.variantData;
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = this.querySelectorAll(".f-product__media");
        if (allMedia) {
          allMedia.forEach((media) => {
            if (media.dataset.mediaType === "image") {
              dataSource.push({
                id: media.dataset.mediaIndex,
                src: media.dataset.src,
                width: media.dataset.pswpWidth,
                height: media.dataset.pswpHeight,
                mediaType: media.dataset.mediaType,
              });
            }
            if (media.dataset.mediaType === "model") {
              const html = `<div class="pswp__item--${
                media.dataset.mediaType
              }">${media.querySelector("product-model").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }

            if (
              media.dataset.mediaType === "video" ||
              media.dataset.mediaType == "external_video"
            ) {
              const html = `<div class="pswp__item--${
                media.dataset.mediaType
              }">${media.querySelector("deferred-media").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }
          });
        }

        const options = {
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        };

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, data, index) => {
          const el = this.querySelector(
            '[data-media-index="' + data.id + '"] img'
          );
          if (el) {
            return el;
          }
          return thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, slide) => {
          const el = this.querySelector(
            '[data-media-index="' + slide.data.id + '"] img'
          );
          if (el) {
            return el.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on("change", () => {
          const currIndex = this.lightbox.pswp.currIndex;
          if (!this.onlyImage) {
            const slider =
              this.domNodes.mediaGallery.slider &&
              this.domNodes.mediaGallery.slider.instance;
            if (slider) {
              setTimeout(() => {
                slider.select(currIndex, false, true);
              }, 300);
            }
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          const currSlide = this.lightbox.pswp.currSlide.data;
          if (
            currSlide.mediaType === "video" ||
            currSlide.mediaType === "model" ||
            currSlide.mediaType === "external_video"
          ) {
            e.preventDefault();
          }
        });

        this.lightbox.on("uiRegister", () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: "next",
              ariaLabel: "Next slide",
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: "prev",
              ariaLabel: "Previous slide",
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }

          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="f-icon-svg f-icon--medium  f-icon-close"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        addEventDelegate({
          selector: this.selectors.toggleZoom[0],
          context: this,
          handler: (e, media) => {
            const mediaContainer = media.closest(".f-product__media");
            const mediaType = mediaContainer
              ? mediaContainer.dataset.mediaType
              : media.dataset.mediaType;
            const mediaIndex =
              Number((mediaContainer || media).dataset.mediaIndex) || 0;

            if (FoxThemeSettings.isMobile) {
              const currentSlide = this.mediaGallery
                ? this.mediaGallery.selectedElement
                : null;
              const currentIndex = currentSlide
                ? Number(currentSlide.dataset.mediaIndex)
                : -1;

              if (mediaIndex !== currentIndex) {
                e.preventDefault();
                return false;
              }

              if (mediaType === "video" || mediaType === "external_video") {
                e.preventDefault();
                return false;
              }
            }

            const isImage = mediaType === "image";
            if (isImage) {
              this.lightbox.loadAndOpen(mediaIndex);
            }
          },
        });
      }

      onSlideChanged(index) {
        this.isDraggable = true;
        this.selectedSlide = this.mediaGallery.selectedElement;
        this.selectedMediaType = this.selectedSlide.dataset.mediaType;
        this.selectedMediaId = this.selectedSlide.dataset.mediaId;

        const mediaAspectRatio = this.selectedSlide.dataset.aspectRatio;
        this.domNodes.viewer.style.setProperty(
          "--media-aspect-ratio",
          mediaAspectRatio
        );

        if (
          "model,external_video,video".includes(this.selectedMediaType) &&
          this.isDraggable
        ) {
          this.toggleDraggable();
        } else {
          this.toggleDraggable(true);
        }

        if (this.selectedMediaType === "model") {
          this.domNodes.xrButton &&
            this.domNodes.xrButton.setAttribute(
              "data-shopify-model3d-id",
              this.selectedMediaId
            );
        }

        if (this.thumbnails) {
          this.thumbnails.select(index);
        }
        this.syncVerticalThumbnail(index);

        if (this.domNodes.sliderCounter) {
          this.domNodes.sliderCounter.textContent = index + 1;
        }

        this.domNodes.mediaGallery.classList.remove("pointer-move");

        this.playActiveMedia(this.selectedSlide);

        if (
          this.section &&
          typeof this.section.syncVariantFromFeaturedMedia === "function"
        ) {
          this.section.syncVariantFromFeaturedMedia(this.selectedMediaId);
        }
      }

      handleScreenChange() {
        document.addEventListener("matchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length > 4) {
            this.thumbnails.options.wrapAround = true;
            this.domNodes.thumbnails.update();
          }
        });
        document.addEventListener("unmatchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length < 6) {
            this.thumbnails.options.wrapAround = false;
            this.domNodes.thumbnails.update();
          }
        });
        if (this.thumbnails.cells.length > 5) {
          this.domNodes.thumbnails.update();
          this.thumbnails.options.wrapAround = true;
          this.domNodes.thumbnails.update();
        }
      }

      // Flickity has no vertical mode, so above 768px the thumbnails Flickity is
      // switched off by CSS (md:flickity-disable) and the list becomes a native
      // vertical scroller (wheel / trackpad / touch) with drag-to-scroll on top.
      initVerticalThumbnails() {
        const list = this.domNodes.thumbnails;
        if (!list || this.verticalThumbsInitialized) return;
        this.verticalThumbsInitialized = true;

        addEventDelegate({
          context: list,
          selector: ".f-product__media-thumbnails-item",
          handler: (event, target) => {
            // While Flickity is active its own staticClick handles the click.
            if (this.isThumbnailsSliderActive()) return;
            // Ignore the click that ends a drag.
            if (Date.now() - (this.thumbnailsDragEndedAt || 0) < 300) return;
            const index = Number(target.dataset.thumbnailIndex);
            if (Number.isNaN(index)) return;
            this.mediaGallery && this.mediaGallery.select(index, false, false);
          },
        });

        // Without asNavFor the active (mobile) slider needs its own click handling.
        if (this.thumbnails) {
          this.thumbnails.on(
            "staticClick",
            (event, pointer, cellElement, cellIndex) => {
              if (cellIndex === undefined || !this.mediaGallery) return;
              this.mediaGallery.select(cellIndex, false, false);
            }
          );

          // Crossing the md breakpoint turns the thumbnails Flickity on or off.
          // The desktop column sets is-selected by hand, which Flickity neither
          // knows about nor clears, so hand the selection over both ways.
          this.thumbnails.on("activate", () => {
            // "activate" fires before Flickity selects its initial cell.
            Promise.resolve().then(() => {
              if (!this.isThumbnailsSliderActive()) return;
              list
                .querySelectorAll(".f-product__media-thumbnails-item.is-selected")
                .forEach((item) => item.classList.remove("is-selected"));
              this.thumbnails.select(
                (this.mediaGallery && this.mediaGallery.selectedIndex) || 0,
                false,
                true
              );
            });
          });
          this.thumbnails.on("deactivate", () => {
            this.syncVerticalThumbnail(
              (this.mediaGallery && this.mediaGallery.selectedIndex) || 0
            );
          });
        }

        this.initVerticalThumbnailsDrag(list);

        this.syncVerticalThumbnail(
          (this.mediaGallery && this.mediaGallery.selectedIndex) || 0
        );
      }

      initVerticalThumbnailsDrag(list) {
        let startY = 0;
        let startScrollTop = 0;
        let pointerId = null;
        let dragged = false;

        const onPointerMove = (event) => {
          const distance = startY - event.clientY;
          if (!dragged) {
            if (Math.abs(distance) <= 4) return;
            dragged = true;
            this.thumbnailsDragging = true;
            list.classList.add("is-dragging");
            // Captured only once a real drag starts: capturing on pointerdown
            // retargets the click to the list and breaks thumbnail clicks.
            list.setPointerCapture && list.setPointerCapture(event.pointerId);
          }
          list.scrollTop = startScrollTop + distance;
        };

        const onPointerUp = (event) => {
          if (pointerId === null) return;
          if (dragged) this.thumbnailsDragEndedAt = Date.now();
          this.thumbnailsDragging = false;
          list.classList.remove("is-dragging");
          if (list.hasPointerCapture && list.hasPointerCapture(pointerId)) {
            list.releasePointerCapture(pointerId);
          }
          list.removeEventListener("pointermove", onPointerMove);
          pointerId = null;
          dragged = false;
        };

        list.addEventListener("pointerdown", (event) => {
          // Touch scrolls natively, Flickity owns the pointer while it is active.
          if (event.pointerType === "touch" || event.button !== 0) return;
          if (this.isThumbnailsSliderActive()) return;

          pointerId = event.pointerId;
          dragged = false;
          startY = event.clientY;
          startScrollTop = list.scrollTop;
          list.addEventListener("pointermove", onPointerMove);
        });

        list.addEventListener("pointerup", onPointerUp);
        list.addEventListener("pointercancel", onPointerUp);
        // preventDefault here instead of on pointerdown, which would swallow the
        // compatibility mouse events the thumbnail click relies on.
        list.addEventListener("dragstart", (event) => event.preventDefault());
      }

      isThumbnailsSliderActive() {
        return !!(this.thumbnails && this.thumbnails.isActive);
      }

      syncVerticalThumbnail(index) {
        if (!this.isVerticalThumbs || this.isThumbnailsSliderActive()) return;
        const list = this.domNodes.thumbnails;
        if (!list) return;
        // Read the DOM: variant group images can swap the thumbnails around.
        const items = Array.from(
          list.querySelectorAll(".f-product__media-thumbnails-item")
        );
        if (!items.length) return;

        items.forEach((item, i) =>
          item.classList.toggle("is-selected", i === index)
        );

        const active = items[index];
        if (!active || this.thumbnailsDragging) return;
        // Scroll the column only, scrollIntoView would move the page too.
        list.scrollTo({
          top: active.offsetTop - (list.clientHeight - active.offsetHeight) / 2,
          behavior: "smooth",
        });
      }

      toggleDraggable(status = false) {
        // NOTE: do not assign to `this.draggable` here. `this` is the
        // <media-gallery> HTMLElement, and `draggable` is a native reflected
        // HTML attribute, not a free app flag. Setting it makes the whole
        // element (including the vertical thumbnails column nested inside
        // it) a native HTML5 drag source, which steals the pointer session
        // (fires pointercancel) from any custom drag-to-scroll happening
        // inside it, such as the vertical thumbnails column.
        this.isDraggable = status;
        this.mediaGallery.options.draggable = status;
        this.mediaGallery.updateDraggable();
      }
      playActiveMedia(selected) {
        window.pauseAllMedia();
        const deferredMedia = selected.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(mediaId) {
        const selectedMedia = this.domNodes.mediaGallery.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!selectedMedia) return;
        const mediaIndex = Number(selectedMedia.dataset.mediaIndex);

        if (this.isCarousel || FoxThemeSettings.isMobile) {
          // FoxTheme.Slider: 3rd arg true = positionSliderAtSelected (no animation);
          // false = startAnimation (same smooth motion as arrow buttons).
          this.mediaGallery &&
            this.mediaGallery.select(mediaIndex, false, false);
        } else {
          this.scrollIntoView(selectedMedia);
        }
      }

      scrollIntoView(selectedMedia) {
        if (FoxThemeSettings.isMobile) return;
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  );
}
